// Migration Pacing & Service Desk Impact — Cyan Room design system

import { pbAuthedFetch } from '../authHeaders';
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

export function PacingPage() {
  const { data: pacing, isLoading: pacingLoading, error: pacingError } = useQuery({
    queryKey: ['pacing'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/pacing');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const [velocityPct, setVelocityPct] = useState(100);
  const [devicesPerWeek, setDevicesPerWeek] = useState(500);
  const [tier1Agents, setTier1Agents] = useState(48);

  useEffect(() => {
    if (pacing) {
      setDevicesPerWeek(Math.round(pacing.velocity_median) || 500);
      setTier1Agents(pacing.agent_count || 48);
    }
  }, [pacing]);

  const { data: scenarioData } = useQuery({
    queryKey: ['pacing-scenario', velocityPct],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/pacing/scenario?velocity=${velocityPct}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !pacingLoading && !pacingError,
  });

  const velocityPctForThrottle = pacing?.velocity_median ? (devicesPerWeek / pacing.velocity_median) * 100 : 0;
  const { data: throttleScenario } = useQuery({
    queryKey: ['pacing-throttle', devicesPerWeek, tier1Agents],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/pacing/scenario?velocity=${velocityPctForThrottle}&agents=${tier1Agents}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !!pacing && velocityPctForThrottle > 0,
  });

  const { data: lagData } = useQuery({
    queryKey: ['pacing-lag'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/pacing/lag');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !pacingLoading && !pacingError,
  });

  const { data: multiCampaign } = useQuery({
    queryKey: ['pacing-multi-campaign'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/pacing/multi-campaign');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    enabled: !pacingLoading && !pacingError,
  });

  const weeksObserved = pacing?.weeks_observed ?? 0;
  const insufficientData = weeksObserved < 3;

  const forecast = pacing?.forecast || [];
  const correlationSeries = lagData?.series || [];
  const multiCampaignSeries = multiCampaign?.weeks || [];
  const collisionWeeks = multiCampaign?.collision_weeks || [];

  const scenario = useMemo(() => {
    if (!scenarioData) return null;
    return {
      current: scenarioData.current,
      proposed: scenarioData.proposed,
      delta: scenarioData.delta,
    };
  }, [scenarioData]);

  if (pacingLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Migration</p>
              <h1 className="pagehead__title">Pacing &amp; Service Desk Impact</h1>
              <div className="pagehead__facts">
                <span className="fact">How fast can we migrate without breaking the service desk?</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading pacing data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (pacingError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Migration</p>
              <h1 className="pagehead__title">Pacing &amp; Service Desk Impact</h1>
              <div className="pagehead__facts">
                <span className="fact">How fast can we migrate without breaking the service desk?</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load pacing data</h3>
                <p className="state__body">
                  Could not connect to the pacing API. Please check your connection and try again.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Migration</p>
            <h1 className="pagehead__title">Pacing &amp; Service Desk Impact</h1>
            <div className="pagehead__facts">
              <span className="fact">How fast can we migrate without breaking the service desk?</span>
              {weeksObserved > 0 && (
                <span className="fact">Observation: <b>{weeksObserved} wk{weeksObserved !== 1 ? 's' : ''}</b></span>
              )}
            </div>
          </div>
          <div className="box__head-actions">
            <button className="btn btn--secondary btn--sm" onClick={() => window.askStella?.('Explain the migration capacity model and what you recommend for the next wave.')}>Ask Stella about this</button>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Insufficient data guard */}
        {insufficientData && (
          <div className="notice notice--warn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div>
              <p className="notice__title">Insufficient observation window</p>
              <p className="notice__body">
                {weeksObserved} of 3 minimum weeks observed. Pacing model suppressed.
                Projections and scenario modeling are hidden until the observation window
                reaches the minimum threshold.
              </p>
            </div>
          </div>
        )}

        {/* Core metrics row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Pacing metrics">
          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Tickets / Device</p>
              <p className="ind__full">Tickets per Migrated Device</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">
                {pacing?.tickets_per_device != null
                  ? pacing.tickets_per_device.toFixed(2)
                  : '—'}
              </p>
            </div>
            <p className="ind__reading">
              <i></i>
              {pacing?.ci_lower != null && pacing?.ci_upper != null
                ? <>CI: <b>{pacing.ci_lower.toFixed(2)}–{pacing.ci_upper.toFixed(2)}</b></>
                : <>CI: <b>—</b></>}
              {pacing?.sample_size != null ? <> · n={pacing.sample_size.toLocaleString()}</> : ''}
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Hrs / 100 Dev</p>
              <p className="ind__full">Hours Consumed per 100 Devices</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">
                {pacing?.median_hours_per_100 != null
                  ? pacing.median_hours_per_100.toFixed(0)
                  : '—'}
              </p>
              {pacing?.p90_hours_per_100 != null && (
                <span className="ind__delta">p90: {pacing.p90_hours_per_100.toFixed(0)}</span>
              )}
            </div>
            <p className="ind__reading">
              <i></i>
              <b>Median</b> service desk hours per 100 devices migrated
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Headroom</p>
              <p className="ind__full">Current Headroom</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">
                {pacing?.headroom_hours != null
                  ? `${pacing.headroom_hours.toLocaleString()} hrs`
                  : '—'}
              </p>
            </div>
            <p className="ind__reading">
              <i></i>
              {pacing?.headroom_pct != null
                ? <><b>{pacing.headroom_pct.toFixed(0)}%</b> remaining</>
                : '—'}
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Max Velocity</p>
              <p className="ind__full">Max Sustainable Velocity</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">
                {pacing?.velocity_median != null && pacing?.velocity_p90 != null
                  ? `${pacing.velocity_median}–${pacing.velocity_p90}`
                  : '—'}
              </p>
              <span className="ind__delta">dev/wk</span>
            </div>
            <p className="ind__reading">
              <i></i>
              Devices/week range (median MHT – p90 MHT)
            </p>
          </div>
        </div>

        {/* Queue Depth Projection Chart */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Forecast</p>
              <h2 className="box__title">Queue Depth Projection — 12-Week Forecast</h2>
            </div>
            {forecast.length > 0 && (
              <div className="box__head-actions">
                <span className="fact">Weeks: {forecast[0]?.week}–{forecast[forecast.length - 1]?.week}</span>
              </div>
            )}
          </div>
          <div className="box__body">
            {insufficientData ? (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Projections suppressed</h3>
                <p className="state__body">
                  Insufficient observation window. Projections require at least 3 weeks of data.
                </p>
              </div>
            ) : forecast.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={forecast} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                  <XAxis dataKey="week" tick={{ fontSize: 12, fill: 'var(--ink-45)' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fill: 'var(--ink-45)' }} label={{ value: 'Tickets', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'var(--ink-45)' } }} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--hair)', borderRadius: 'var(--r-ctl)', fontSize: 13 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area yAxisId="left" type="monotone" dataKey="projected_load" name="Projected Load" stroke="var(--risk)" fill="var(--risk)" fillOpacity={0.1} isAnimationActive={false} />
                  <Area yAxisId="left" type="monotone" dataKey="projected_capacity" name="Projected Capacity" stroke="var(--ok)" fill="var(--ok)" fillOpacity={0.1} isAnimationActive={false} />
                  <Line yAxisId="left" type="monotone" dataKey="projected_load" name="Load Trend" stroke="var(--risk)" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line yAxisId="left" type="monotone" dataKey="projected_capacity" name="Capacity Trend" stroke="var(--ok)" strokeWidth={2} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="state">
                <h3 className="state__title">No forecast data</h3>
                <p className="state__body">No forecast data available yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Migration Throttle */}
        <div className="box" style={{ marginBottom: 'var(--sp-4)' }}>
          <div className="box__head"><h3 className="box__title">Migration throttle</h3></div>
          <div className="box__body">
            <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
              <label htmlFor="throttle-devices">Devices migrated per week</label>
              <input
                id="throttle-devices"
                type="range"
                min={100}
                max={900}
                step={25}
                value={devicesPerWeek}
                onChange={(e) => setDevicesPerWeek(parseInt(e.target.value, 10))}
              />
              <span>{devicesPerWeek}</span>
            </div>
            <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
              <label htmlFor="throttle-agents">Tier 1 agents</label>
              <input
                id="throttle-agents"
                type="range"
                min={36}
                max={76}
                step={1}
                value={tier1Agents}
                onChange={(e) => setTier1Agents(parseInt(e.target.value, 10))}
              />
              <span>{tier1Agents}</span>
            </div>
            {throttleScenario?.capacity && throttleScenario.capacity.headroom_hours <= 0 && (
              <div className="notice notice--warn">
                <p className="notice__body">
                  The service desk has no spare capacity for migration at the current BAU ticket volume — headroom is {throttleScenario.capacity.headroom_pct}%. No devices/week rate is sustainable at {throttleScenario.capacity.agent_count}-agent Tier 1 staffing until BAU volume drops or headcount grows.
                </p>
              </div>
            )}
            {pacing && pacing.velocity_median > 0 && !throttleScenario && (
              <div className="page-loading" role="status" aria-live="polite">Computing utilization…</div>
            )}
            {throttleScenario && (
              <>
                <div className="gauge-v">{throttleScenario.proposed.utilization_pct}%</div>
                <p className="cap">Tier 1 utilization at {devicesPerWeek} devices/week with {tier1Agents} agents. Queue depth at 12 weeks: {throttleScenario.proposed.queue_depth_wk12}.</p>
                <table className="tbl">
                  <thead><tr><th>Option</th><th>Utilization</th><th>Queue @ 12wk</th></tr></thead>
                  <tbody>
                    <tr><td>-20% rate</td><td colSpan={2}>See scenario modeler below for named options</td></tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>

        {/* Scenario Modeler */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Modeling</p>
              <h2 className="box__title">Scenario Modeler</h2>
            </div>
          </div>
          <div className="box__body">
            {insufficientData ? (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Scenario modeling suppressed</h3>
                <p className="state__body">
                  Insufficient observation window. Scenario modeling requires at least 3 weeks of data.
                </p>
              </div>
            ) : (
              <div>
                <div className="field" style={{ marginBottom: 'var(--sp-4)' }}>
                  <label>
                    Proposed Weekly Velocity: {velocityPct}% of current
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={200}
                    value={velocityPct}
                    onChange={(e) => setVelocityPct(Number(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--gold)' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)' }}>
                    <span>10%</span>
                    <span>100%</span>
                    <span>200%</span>
                  </div>
                </div>

                {scenario && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
                      {/* Current Plan */}
                      <div className="box box--sub">
                        <div className="box__head">
                          <div className="box__head-text">
                            <p className="box__eyebrow">Baseline</p>
                            <h3 className="box__title" style={{ fontSize: 'var(--text-lg)' }}>Current Plan</h3>
                          </div>
                        </div>
                        <div className="box__body">
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Weekly Ticket Load</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.current?.weekly_ticket_load ?? '—'}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Utilization</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.current?.utilization_pct != null ? `${scenario.current.utilization_pct}%` : '—'}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Queue Depth (Wk 12)</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.current?.queue_depth_wk12 ?? '—'}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Projected Completion</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.current?.projected_completion ?? '—'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Proposed Plan */}
                      <div className="box box--sub">
                        <div className="box__head">
                          <div className="box__head-text">
                            <p className="box__eyebrow">Proposed</p>
                            <h3 className="box__title" style={{ fontSize: 'var(--text-lg)' }}>{velocityPct}% Velocity</h3>
                          </div>
                        </div>
                        <div className="box__body">
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Weekly Ticket Load</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.proposed?.weekly_ticket_load ?? '—'}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Utilization</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.proposed?.utilization_pct != null ? `${scenario.proposed.utilization_pct}%` : '—'}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Queue Depth (Wk 12)</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.proposed?.queue_depth_wk12 ?? '—'}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--mono)', color: 'var(--ink-45)', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Projected Completion</p>
                              <p style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 'var(--text-lg)', margin: 'var(--sp-1) 0 0' }}>{scenario.proposed?.projected_completion ?? '—'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Delta row */}
                    <div className="notice notice--info" style={{ marginTop: 'var(--sp-3)' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      <div style={{ display: 'flex', gap: 'var(--sp-6)', flexWrap: 'wrap', fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>
                        <span>Δ Load: <b>{scenario.delta?.weekly_ticket_load != null ? (scenario.delta.weekly_ticket_load > 0 ? '+' : '') + scenario.delta.weekly_ticket_load : '—'}</b></span>
                        <span>Δ Utilization: <b>{scenario.delta?.utilization_pct != null ? (scenario.delta.utilization_pct > 0 ? '+' : '') + scenario.delta.utilization_pct + '%' : '—'}</b></span>
                        <span>Δ Queue (Wk 12): <b>{scenario.delta?.queue_depth_wk12 != null ? (scenario.delta.queue_depth_wk12 > 0 ? '+' : '') + scenario.delta.queue_depth_wk12 : '—'}</b></span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Correlation Evidence Panel */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Evidence</p>
              <h2 className="box__title">Correlation Evidence — Devices vs Attributed Tickets</h2>
            </div>
          </div>
          <div className="box__body">
            {insufficientData ? (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Correlation analysis suppressed</h3>
                <p className="state__body">
                  Insufficient observation window. Correlation analysis requires at least 3 weeks of data.
                </p>
              </div>
            ) : correlationSeries.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={correlationSeries} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                    <XAxis dataKey="week" tick={{ fontSize: 12, fill: 'var(--ink-45)' }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12, fill: 'var(--ink-45)' }} label={{ value: 'Devices Completed', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'var(--ink-45)' } }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: 'var(--ink-45)' }} label={{ value: 'Attributed Tickets', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: 'var(--ink-45)' } }} />
                    <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--hair)', borderRadius: 'var(--r-ctl)', fontSize: 13 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="devices_completed" name="Devices Completed/Week" fill="var(--gold)" fillOpacity={0.7} isAnimationActive={false} />
                    <Line yAxisId="right" type="monotone" dataKey="attributed_tickets" name="Attributed Tickets/Week" stroke="var(--risk)" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>

                <div className="notice notice--info" style={{ marginTop: 'var(--sp-3)' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>
                    <strong>Best-fit lag:</strong> {lagData?.lag_weeks ?? '—'} weeks.
                    {' '}<strong>Correlation:</strong> r={lagData?.r != null ? lagData.r.toFixed(3) : '—'}
                    {lagData?.ci_lower != null && lagData?.ci_upper != null
                      ? ` (CI: ${lagData.ci_lower.toFixed(3)}–${lagData.ci_upper.toFixed(3)}`
                      : ' (CI: —'}
                    {lagData?.n != null ? `, n=${lagData.n})` : ')'}
                  </div>
                </div>

                <p className="notice__body" style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--text-sm)', color: 'var(--ink-45)', fontStyle: 'italic' }}>
                  Correlation is observed, not causal. Confounders may include seasonal volume,
                  concurrent campaigns, and reporting changes.
                </p>
              </div>
            ) : (
              <div className="state">
                <h3 className="state__title">No correlation data</h3>
                <p className="state__body">No correlation data available yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Multi-Campaign Load */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Campaigns</p>
              <h2 className="box__title">Multi-Campaign Combined Load</h2>
            </div>
            {multiCampaign?.active_campaigns?.length > 0 && (
              <div className="box__head-actions">
                <span className="pill pill--warn"><i></i> {multiCampaign.active_campaigns.length} active campaigns</span>
              </div>
            )}
          </div>
          <div className="box__body">
            {multiCampaignSeries.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={multiCampaignSeries} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                    <XAxis dataKey="week" tick={{ fontSize: 12, fill: 'var(--ink-45)' }} />
                    <YAxis tick={{ fontSize: 12, fill: 'var(--ink-45)' }} label={{ value: 'Devices/Week', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'var(--ink-45)' } }} />
                    <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--hair)', borderRadius: 'var(--r-ctl)', fontSize: 13 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {(multiCampaign?.campaign_names || []).map((name: string, i: number) => {
                      const colors = ['var(--gold)', 'var(--gold-soft)', 'var(--ok)', 'var(--risk)', 'var(--navy)'];
                      return (
                        <Area
                          key={name}
                          type="monotone"
                          dataKey={name}
                          name={name}
                          stackId="1"
                          stroke={colors[i % colors.length]}
                          fill={colors[i % colors.length]}
                          fillOpacity={0.4}
                          isAnimationActive={false}
                        />
                      );
                    })}
                    {collisionWeeks.map((w: number) => (
                      <Line
                        key={`collision-${w}`}
                        type="monotone"
                        dataKey={() => null}
                        stroke="transparent"
                        dot={false}
                        isAnimationActive={false}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
                {collisionWeeks.length > 0 && (
                  <div className="notice notice--warn" style={{ marginTop: 'var(--sp-2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>
                      <strong>Collision weeks:</strong> {collisionWeeks.join(', ')}
                      <span style={{ color: 'var(--ink-45)' }}> — multiple campaigns at peak velocity simultaneously.</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="state">
                <h3 className="state__title">No multi-campaign data</h3>
                <p className="state__body">No multi-campaign load data available yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
